import { Grid } from '../../core/Grid.js';
import { FloodGame } from './FloodGame.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha, canvasFont } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, MOTION, staggerDelay } from '../../rendering/juice.js';

export interface FloodRenderConfig extends Partial<RenderConfig> {
    wallColor?: string;
    wallWidth?: number;
    waterColor?: string;
    faucetColor?: string;
    fillTimeColor?: string;
    warningColor?: string;
}

const FLOOD_DEFAULTS = {
    wallColor: COLORS.teal,
    wallWidth: 4,
    waterColor: colorWithAlpha('blue', 0.4),
    faucetColor: COLORS.blue,
    fillTimeColor: COLORS.amber,
    warningColor: COLORS.amber
};

// ── Wall animation (docs/juice.md) ────────────────────────────────────
//
// A wall is a segment of grid line, so it is drawn on the way Line's is: out of
// the end that already carries wall, since you build a barrier by extending one.
// With both ends occupied (closing a gap) or neither (a stray piece) it opens
// from its midpoint instead.
//
// The water is *not* animated. It is simulation, and this first pass leaves it
// alone — see docs/juice.md on `replay`.

type WallOrigin = 'a' | 'b' | 'mid';

interface WallTween {
    kind: 'enter' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    origin: WallOrigin;
    /** Exit only: the colour it had on the last frame it existed. */
    color: string;
}

export class FloodRenderer extends GridRenderer {
    private floodConfig: typeof FLOOD_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private wallTweens = new Map<string, WallTween>();
    private wallState = new Set<string>();          // what the last frame drew
    private wallColors = new Map<string, string>(); // last colour, for removals
    private wallsSynced: boolean = false;           // first frame seeds, never animates
    private faucetCol: number | null = null;        // where the faucet is now
    private faucetEnter: number | null = null;      // it growing in there
    private faucetExit: { col: number; start: number } | null = null;  // the one it left
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: FloodGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: FloodRenderConfig = {}) {
        super(canvas, grid, config);
        this.floodConfig = { ...FLOOD_DEFAULTS, ...config };
        // The water fill is a solid wash of blue — the cursor needs a backing stroke
        // to stay readable over it.
        this.keyboardCursorHalo = true;
        // The top rim is the faucet row: show a faucet-style marker there.
        this.keyboardFineTopRimSlots = true;
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every wall and the faucet at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.wallTweens.clear();
        this.faucetEnter = null;
        this.faucetExit = null;
    }

    render(game: FloodGame): void {
        const grid = game.getGrid();

        this.lastGame = game;
        this.animating = false;
        this.syncWallTweens(game.getWalls());
        this.syncFaucetTween(game.getFaucetCol());

        this.clear();
        this.drawGrid();
        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }
        this.drawReachableCells(game);
        this.drawWater(game);
        if (isVortexGame(game)) { this.drawActiveVortexGates(game); }
        this.drawFillTimes(game);
        this.drawWalls(game);
        this.drawOuterWall();
        this.drawFaucet(game);
        this.drawDropHints(game);
        this.drawDigitsAndBadges(grid, game);
        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Wall / faucet animation (docs/juice.md) ───────────────────────

    /**
     * Diff the walls against the last frame and start a tween per change. A bulk
     * change (Reset, Restore best) fans out in reading order.
     */
    private syncWallTweens(walls: Set<string>): void {
        if (!this.tweens.isEnabled()) {
            this.wallTweens.clear();
            this.wallState = new Set(walls);
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a saved
        // solution would rebuild the whole barrier.
        if (!this.wallsSynced) {
            this.wallState = new Set(walls);
            this.wallsSynced = true;
            return;
        }

        const added: string[] = [];
        const removed: string[] = [];
        for (const key of walls) if (!this.wallState.has(key)) added.push(key);
        for (const key of this.wallState) if (!walls.has(key)) removed.push(key);
        if (added.length === 0 && removed.length === 0) return;

        const rank = (key: string) => {
            const edge = this.grid.deserializeEdge(key);
            return edge ? edge.pa.row * this.grid.size + edge.pa.col : 0;
        };
        const changed = [...added, ...removed].sort((a, b) => rank(a) - rank(b));

        const now = this.tweens.now();
        changed.forEach((key, i) => {
            const start = now + staggerDelay(i, changed.length);
            if (walls.has(key)) {
                // Grow out of the barrier as it stood *before* this piece joined it.
                this.wallTweens.set(key, {
                    kind: 'enter', start,
                    origin: this.wallOrigin(key, this.wallState, true), color: ''
                });
            } else {
                // Retract toward whatever is left of it now this piece has gone.
                this.wallTweens.set(key, {
                    kind: 'exit', start, origin: this.wallOrigin(key, walls, false),
                    color: this.wallColors.get(key) ?? COLORS.teal
                });
            }
        });

        this.wallState = new Set(walls);
    }

    /**
     * Which end a wall grows from.
     *
     * A keyboard trace hop lays the wall it crosses and lands the cursor on the far
     * intersection, so that end is where the gesture arrived: a new wall grows *away*
     * from it and a removed one retracts *into* it. That has to be asked first —
     * connectivity alone gets it backwards whenever you trace *toward* wall that is
     * already there, which is exactly when you are joining a run up.
     *
     * Off a gesture it falls back to the board *without* this piece: the end that
     * already carries wall, or the midpoint when both ends do (closing a gap) or
     * neither does (a piece out on its own). Edge keys are two serialized points.
     */
    private wallOrigin(key: string, others: Set<string>, entering: boolean): WallOrigin {
        const cursor = this.keyboardIntersection();
        const edge = cursor ? this.grid.deserializeEdge(key) : null;
        if (cursor && edge) {
            const atA = edge.pa.row === cursor.row && edge.pa.col === cursor.col;
            const atB = edge.pb.row === cursor.row && edge.pb.col === cursor.col;
            if (atA !== atB) {
                if (entering) return atA ? 'b' : 'a';
                return atA ? 'a' : 'b';
            }
        }

        const ka = key.slice(0, 2);
        const kb = key.slice(2, 4);
        let atA = false, atB = false;
        for (const other of others) {
            if (other === key) continue;
            const oa = other.slice(0, 2), ob = other.slice(2, 4);
            if (oa === ka || ob === ka) atA = true;
            if (oa === kb || ob === kb) atB = true;
            if (atA && atB) break;
        }
        if (atA === atB) return 'mid';
        return atA ? 'a' : 'b';
    }

    /** How much of a wall is on the board this frame, and from which end. */
    private wallExtent(
        tween: WallTween, now: number
    ): { origin: WallOrigin; grown: number; done: boolean } {
        const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
        // The `exit` curve runs the *disappearance*, so invert it.
        return { origin: tween.origin, grown: tween.kind === 'enter' ? value : 1 - value, done };
    }

    /**
     * Moving the faucet is a placement, not a journey: it is pulled up at the old
     * column as it is planted at the new one, both at once, with the shorter `exit`
     * keeping the overlap brief.
     */
    private syncFaucetTween(col: number): void {
        if (col === this.faucetCol) return;
        if (this.faucetCol === null || !this.tweens.isEnabled()) {
            this.faucetEnter = null;
            this.faucetExit = null;
        } else {
            const now = this.tweens.now();
            this.faucetExit = { col: this.faucetCol, start: now };
            this.faucetEnter = now;
        }
        this.faucetCol = col;
    }

    /** Gray wall around the grid rim, matching the player wall thickness. */
    private drawOuterWall(): void {
        const { padding, cellSize } = this.config;
        const rim = padding + this.grid.size * cellSize;

        this.ctx.strokeStyle = COLORS.gray;
        this.ctx.lineWidth = this.floodConfig.wallWidth;
        this.ctx.lineJoin = 'round';
        this.ctx.lineCap = 'round';
        this.ctx.beginPath();
        this.ctx.rect(padding, padding, rim - padding, rim - padding);
        this.ctx.stroke();
    }

    /**
     * Draw amber spill hints: a quarter-circle arrow at each cell where water
     * tips over an edge during the simulation, curving from the incoming
     * horizontal side down toward the fall.
     */
    private drawDropHints(game: FloodGame): void {
        const { padding, cellSize } = this.config;
        const hints = game.getDropHints();
        if (hints.length === 0) return;

        const r = cellSize * 0.42;
        this.ctx.save();
        this.ctx.strokeStyle = COLORS.amber;
        this.ctx.fillStyle = COLORS.amber;
        this.ctx.lineWidth = 3;
        this.ctx.lineCap = 'round';
        this.ctx.setLineDash([]);

        for (const hint of hints) {
            const x0 = padding + hint.col * cellSize;
            const y0 = padding + hint.row * cellSize;

            if (hint.from === null) {
                // Straight-down spill (no clear horizontal source).
                const cx = x0 + cellSize / 2;
                this.ctx.beginPath();
                this.ctx.moveTo(cx, y0 + cellSize * 0.18);
                this.ctx.lineTo(cx, y0 + cellSize * 0.78);
                this.ctx.stroke();
                this.drawDropArrowHead(cx, y0 + cellSize * 0.78, 0, 1);
                continue;
            }

            // Quarter pipe centered on the bottom corner of the incoming side, so
            // the arc meets the side edge horizontally and the bottom edge
            // vertically. It bulges toward the cell center; the opening faces the
            // incoming corner (away from center). The whole glyph (arc + head) is
            // shifted a touch horizontally toward the incoming corner.
            const shift = cellSize * 0.07;
            const dir = hint.from === 'W' ? -1 : 1;
            const cornerX = (hint.from === 'W' ? x0 : x0 + cellSize) + dir * shift;
            const cornerY = y0 + cellSize;
            const aEntry = -Math.PI / 2;                    // side edge (north of corner)
            const aExit = hint.from === 'W' ? 0 : Math.PI;  // bottom edge
            this.ctx.beginPath();
            this.ctx.arc(cornerX, cornerY, r, aEntry, aExit, hint.from === 'E');
            this.ctx.stroke();

            // Arrowhead at the bottom endpoint, pointing down, nudged a hair toward
            // the arc center so the tip sits just inside the sweep.
            const nudge = cellSize * 0.015;
            const exitX = hint.from === 'W' ? cornerX + r - nudge : cornerX - r + nudge;
            this.drawDropArrowHead(exitX, cornerY, 0, 1);
        }

        this.ctx.restore();
    }

    /** Filled triangle arrowhead at (x, y) pointing along unit vector (ux, uy). */
    private drawDropArrowHead(x: number, y: number, ux: number, uy: number): void {
        const { cellSize } = this.config;
        const len = cellSize * 0.22;
        const wid = cellSize * 0.09;
        const perpx = -uy, perpy = ux;
        const tipX = x + ux * len * 0.6, tipY = y + uy * len * 0.6;
        const backX = x - ux * len * 0.4, backY = y - uy * len * 0.4;

        this.ctx.beginPath();
        this.ctx.moveTo(tipX, tipY);
        this.ctx.lineTo(backX + perpx * wid, backY + perpy * wid);
        this.ctx.lineTo(backX - perpx * wid, backY - perpy * wid);
        this.ctx.closePath();
        this.ctx.fill();
    }

    private drawReachableCells(game: FloodGame): void {
        // Only draw when there are unreachable cells (to show what IS reachable)
        if (!game.getHasUnreachableCells()) return;

        const reachable = game.getReachableCells();

        // Draw reachable cells in amber with low alpha
        for (const ck of reachable) {
            const [row, col] = ck.split(',').map(Number);
            this.highlightCell({ row, col }, colorWithAlpha('amber', 0.25));
        }
    }

    private drawWater(game: FloodGame): void {
        const { padding, cellSize } = this.config;

        // Max fill time is when the entire grid is filled
        let maxFillTime = this.grid.size * this.grid.size; 

        // Draw water with variable alpha based on fill time
        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                const fillTime = game.getCellFillTime(row, col);
                if (fillTime !== null) {
                    // Earlier fills are more opaque, later fills more transparent
                    // alpha ranges from 0.3 (early) to 0 (late)
                    const alpha = maxFillTime > 0
                        ? Math.max(0, 0.3 - (fillTime / maxFillTime) * 0.3)
                        : 0.3;

                    this.ctx.fillStyle = colorWithAlpha('blue', alpha);
                    const x = padding + col * cellSize;
                    const y = padding + row * cellSize;
                    this.ctx.fillRect(x, y, cellSize, cellSize);
                }
            }
        }
    }

    private drawFillTimes(game: FloodGame): void {
        const { padding, cellSize } = this.config;

        this.ctx.font = canvasFont(10);
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';

        // Get digit positions to avoid drawing fill times on them
        const digitPositions = new Set<string>();
        const positions = game.getGrid().getAllDigitPositions();
        for (const [, pos] of positions) {
            digitPositions.add(`${pos.row},${pos.col}`);
        }

        for (let row = 0; row < this.grid.size; row++) {
            for (let col = 0; col < this.grid.size; col++) {
                // Skip digit cells
                if (digitPositions.has(`${row},${col}`)) continue;
                // Skip faucet cell
                if (game.getFaucetCol() === col && row === 0) continue;

                const fillTime = game.getCellFillTime(row, col);

                if (fillTime !== null) {
                    // Skip if left cell is adjacent, and has same fill time (to avoid clutter)
                    const leftFillTime = col > 0 ? game.getCellFillTime(row, col - 1) : null;
                    if (leftFillTime !== null && leftFillTime === fillTime
                        && !game.hasWallBetween({ row, col }, 'W')
                    ) continue;

                    const x = padding + col * cellSize + cellSize / 2;
                    const y = padding + row * cellSize + cellSize / 4;
                    const text = fillTime.toString();

                    // Draw text
                    this.ctx.fillStyle = this.floodConfig.fillTimeColor;
                    this.ctx.fillText(text, x, y);
                }
            }
        }
    }

    /** Scoring overlay: redraw the wall segments (primary) in amber. */
    protected drawScoringPrimary(game: FloodGame): void {
        this.drawWalls(game, COLORS.amber);
    }

    private drawWalls(game: FloodGame, colorOverride?: string): void {
        const { wallWidth } = this.floodConfig;
        const disconnected = game.getDisconnectedWalls();
        const walls = game.getWalls();

        // The scoring overlay redraws the walls in a flat colour on a frozen board:
        // it reads state, it doesn't advance it. (The diff already ran, in render.)
        const live = colorOverride === undefined;
        const now = this.tweens.now();

        this.ctx.lineWidth = wallWidth;
        this.ctx.lineCap = 'round';

        for (const key of walls) {
            const color = colorOverride ?? (disconnected.has(key) ? COLORS.amber : COLORS.teal);
            if (live) this.wallColors.set(key, color);

            const tween = live ? this.wallTweens.get(key) : undefined;
            const shape = tween
                ? this.wallExtent(tween, now)
                : { origin: 'mid' as WallOrigin, grown: 1, done: true };
            if (tween && shape.done) this.wallTweens.delete(key);
            else if (tween) this.animating = true;

            this.ctx.strokeStyle = color;
            this.strokeWall(key, shape.origin, shape.grown);
        }

        // Walls being removed are already gone from game state — they only exist
        // here, retracting in the colour they wore when they went.
        for (const [key, tween] of live ? this.wallTweens : []) {
            if (tween.kind !== 'exit' || walls.has(key)) continue;
            const shape = this.wallExtent(tween, now);
            if (shape.done) {
                this.wallTweens.delete(key);
                this.wallColors.delete(key);
                continue;
            }
            this.animating = true;
            this.ctx.strokeStyle = tween.color;
            this.strokeWall(key, shape.origin, shape.grown);
        }
    }

    /** Draw the `grown` fraction of a wall, extending from `origin`. */
    private strokeWall(key: string, origin: WallOrigin, grown: number): void {
        if (grown <= 0) return;
        const edge = this.grid.deserializeEdge(key);
        if (!edge) return;
        const { padding, cellSize } = this.config;
        const ax = padding + edge.pa.col * cellSize, ay = padding + edge.pa.row * cellSize;
        const bx = padding + edge.pb.col * cellSize, by = padding + edge.pb.row * cellSize;

        const [t0, t1] = origin === 'a' ? [0, grown]
            : origin === 'b' ? [1 - grown, 1]
            : [0.5 - grown / 2, 0.5 + grown / 2];

        this.ctx.beginPath();
        this.ctx.moveTo(ax + (bx - ax) * t0, ay + (by - ay) * t0);
        this.ctx.lineTo(ax + (bx - ax) * t1, ay + (by - ay) * t1);
        this.ctx.stroke();
    }

    private drawFaucet(game: FloodGame): void {
        const now = this.tweens.now();
        const color = game.checkWin() ? COLORS.teal : this.floodConfig.faucetColor;

        // The faucet left behind, shrinking away at its old column.
        if (this.faucetExit) {
            const { value, done } = this.tweens.tween(this.faucetExit.start, MOTION.exit, now);
            if (done) this.faucetExit = null;
            else {
                this.animating = true;
                this.drawFaucetAt(this.faucetExit.col, color, 1 - value);
            }
        }

        let scale = 1;
        if (this.faucetEnter !== null) {
            const { value, done } = this.tweens.tween(this.faucetEnter, MOTION.enter, now);
            if (done) this.faucetEnter = null;
            else {
                this.animating = true;
                scale = value;
            }
        }
        this.drawFaucetAt(game.getFaucetCol(), color, scale);
    }

    /** The faucet on the top rim, at `scale` of its full size about the rim point. */
    private drawFaucetAt(faucetCol: number, color: string, scale: number): void {
        if (scale <= 0.01) return;
        const { padding, cellSize } = this.config;
        const x = padding + faucetCol * cellSize + cellSize / 2;

        this.ctx.save();
        // Scale about the point on the rim the faucet sits at, so it grows out of
        // the wall rather than drifting along it.
        this.ctx.translate(x, padding);
        this.ctx.scale(scale, scale);
        this.ctx.translate(-x, -padding);

        // Break a background wedge through the rim wall so the faucet reads as an
        // opening into it.
        this.ctx.fillStyle = this.config.backgroundColor;
        this.ctx.beginPath();
        this.ctx.moveTo(x - 8, padding - 2);
        this.ctx.lineTo(x + 8, padding - 2);
        this.ctx.lineTo(x, padding + 12);
        this.ctx.closePath();
        this.ctx.fill();

        this.drawRimMarker({
            position: { row: 0, col: faucetCol },
            color,
            entry: true,
            withDrop: true
        });
        this.ctx.restore();
    }

    private drawDigitsAndBadges(grid: Grid, game: FloodGame): void {
        this.drawDigits(grid);

        // Draw fill time badges. Digits filled in order are teal; any that fill out
        // of order are amber — a hint, not an alarm, so no red here (red is kept for
        // the structural wall errors in the status bar).
        const correctDigits = new Set(game.getCorrectDigits());
        for (const [digit, pos] of grid.getAllDigitPositions()) {
            const fillTime = game.getCellFillTime(pos.row, pos.col);
            if (fillTime !== null) {
                const badgeColor = correctDigits.has(digit) ? COLORS.teal : this.floodConfig.warningColor;
                this.drawBadge(pos, 'NE', fillTime.toString(), badgeColor, "md", true);
            }
        }
    }

}
